import CryptoJS from "crypto-js";
import { toast } from "react-toastify";
import { camelCase, isEmpty, snakeCase } from "lodash";

export const validatePhone = (phone) => {
    return /([\+84|84|0]+(3|5|7|8|9|1[2|6|8|9]))+([0-9]{8})\b/.test(phone);
};

export const hashPassword = (password) => {
    return CryptoJS.SHA256(password).toString(CryptoJS.enc.Hex);
};

export const checkUpload = (files) => {
    return (files || []).filter((file) => !file?.id);
};

export const convertUTCToLocal = (utcDateStr) => {
    const date = new Date(utcDateStr); // Create Date object from UTC string

    // Format the date to DD/MM/YYYY using toLocaleDateString with options
    return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

export const objectToQueryString = (params) => {
    if (!isEmpty(params))
        return Object.keys(params)
            .filter(
                (key) =>
                    params[key] !== undefined && params[key] !== null && params[key] !== ""
            ) // Filter out empty values
            .map(
                (key) => encodeURIComponent(key) + "=" + encodeURIComponent(params[key])
            ) // Encode the key and value
            .join("&"); // Join with '&'
};

export function cleanObject(obj) {
    return Object.fromEntries(
        Object.entries(obj).filter(([_, value]) => value !== null && value !== "")
    );
}

export const scrollToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: "smooth",
    });
};

/** Merge keys into current location search; falsy values remove the key (same rules as OrdersPage). */
export const mergeLocationSearchParams = (updates) => {
    const newParams = new URLSearchParams(window.location.search);
    Object.entries(updates).forEach(([key, value]) => {
        if (value) {
            newParams.set(key, String(value));
        } else {
            newParams.delete(key);
        }
    });
    return newParams;
};

/**
 * Replace query string via react-router navigate, optionally smooth-scroll to top.
 * @param {import('react-router-dom').NavigateFunction} navigate
 * @param {Record<string, string | number | boolean | undefined | null>} updates
 * @param {{ replace?: boolean, scroll?: boolean }} [options]
 */
export const navigateWithSearchUpdates = (navigate, updates, options = {}) => {
    const { replace = true, scroll = false } = options;
    const newParams = mergeLocationSearchParams(updates);
    navigate(`?${newParams.toString()}`, { replace });
    if (scroll) {
        scrollToTop();
    }
};

// Function to convert Base64 string to a Blob
export function base64ToFile(base64String, mimeType, filename) {
    // Remove the data URL prefix if present
    const base64Data = base64String.replace(/^data:.+;base64,/, "");
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    // Create a File object, which includes a filename
    return new File([byteArray], filename, { type: mimeType });
}

export function removeDiacritics(str) {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export const formatNumberWithDots = (num) => {
    // Remove any non-digit characters except for the decimal point
    const cleanedValue = (num || "").toString().replace(/[^\d]/g, "");
    // Format the cleaned value with dots for every three digits
    return cleanedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

export const cleanNumber = (num) => {
    return parseFloat((num || "").toString().replace(/[^\d]/g, ""));
};

export function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
}

export function setCookie(name, value, days) {
    let expires = "";
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
}

export function deleteCookie(name) {
    document.cookie = name + '=; Max-Age=-99999999; path=/';
}

export function copyToClipboard(text) {
    try {
        navigator.clipboard
            .writeText(text)
            .then(() => {
                toast({
                    title: "Đã sao chép vào khay nhớ tạm.",
                    description: "Sao chép thành công.",
                });
            })
            .catch(() => {
                toast({
                    title: "Không thể sao chép.",
                    description: "Đã xảy ra lỗi trong quá trình sao chép.",
                });
            });
    } catch (error) {
        toast({
            title: "Không thể sao chép.",
            description: "Có lỗi xảy ra.",
        });
    }
}

export const filterObjectProperties = (obj) => {
    return Object.fromEntries(
        Object.entries(obj).filter(
            ([_, value]) => value !== undefined && value !== null && value !== ""
        )
    );
};

export const IconName = {
    documents: "documents",
    documentCheck: "document-check",
    check: "check",
    delivery: "delivery",
    delivering: "delivering",
    sellerOrder: "seller_order",
    returned: "returned",
    canceled: "canceled",
    confirm: "confirm",
    shieldCheck: "shield-check",
    shoppingCart: "shopping-cart",
    gift: "gift",
    bell: "bell",
    nextPage: "next-page",
    prevPage: "prev-page",
    dotsHorizontal: "dots-horizontal",
    warming1: "warming-1",
    check2: "check-2",
    trash: "trash",
    X: "X",
    mark1: "mark-1",
};

export const convertKeysToCamelCase = (obj) => {
    if (Array.isArray(obj)) {
        return obj.map((item) => convertKeysToCamelCase(item));
    } else if (obj !== null && typeof obj === "object") {
        return Object.keys(obj).reduce((acc, key) => {
            const camelKey = camelCase(key);
            acc[camelKey] = convertKeysToCamelCase(obj[key]);
            return acc;
        }, {});
    }
    return obj;
};
export const convertKeysToSnakeCase = (obj) => {
    if (Array.isArray(obj)) {
        return obj.map((item) => convertKeysToSnakeCase(item));
    } else if (obj !== null && typeof obj === "object") {
        return Object.keys(obj).reduce((acc, key) => {
            const snakeKey = snakeCase(key);
            acc[snakeKey] = convertKeysToSnakeCase(obj[key]);
            return acc;
        }, {});
    }
    return obj;
};

export const formatPrice = (price) => {
    // Remove any non-digit characters except the decimal point
    const cleanPrice = price.replace(/[^\d.]/g, '');

    // Parse the price, assuming it's in the format "XX.XXX"
    const numPrice = parseFloat(cleanPrice);

    if (isNaN(numPrice)) {
        return '0 đ';
    } else {
        // Format the number with thousands separator and add the currency symbol
        return numPrice.toLocaleString('vi-VN') + ' đ';
    }
};
export const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(amount);
};

export const formatDate = (isoString) => {
    const date = new Date(isoString)
    return date
        .toLocaleString("vi-VN", {
            hour: "2-digit",
            minute: "2-digit",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour12: false,
        })
        .replace(",", "")
}
export const formatDateTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr);
    return {
        day: date.toLocaleDateString('vi-VN'),
        time: date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
    };
};
export const formatDay = (isoString) => {
    const date = new Date(isoString)
    return date
        .toLocaleString("vi-VN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour12: false,
        })
        .replace(",", "")
}